<template>
    <main class="reaction-overview">
        <div v-if="loading" class="reaction-overview__state">Sollicitatie laden…</div>

        <div v-else-if="errorMessage" class="reaction-overview__state reaction-overview__state--error">
            {{ errorMessage }}
        </div>

        <div v-else-if="reaction" class="reaction-overview__container">
          <router-link
              class="reaction-overview__back"
              :to="{
                    name: 'grid',
                    params: {
                        gridDefinition: 'grid',
                        bundle: 'reaction',
                    },
                }"
          >
            <i class="ak-icon">west</i>
            Terug naar kandidaten
          </router-link>

            <header class="reaction-overview__header">
                <div>
                    <h1 class="reaction-overview__title">{{ candidateName }}</h1>
                    <p class="reaction-overview__subtitle">
                        Solliciteerde voor <strong>{{ job.title }}</strong>
                        <button
                            v-if="canChangeJob"
                            type="button"
                            class="reaction-overview__job-edit-button"
                            aria-label="Vacature wijzigen"
                            title="Vacature wijzigen"
                            @click="editReactionJob"
                        >
                            <i class="ak-icon" aria-hidden="true">edit</i>
                        </button>
                    </p>
                </div>

                <div class="reaction-overview__status-control">
                    <button
                        type="button"
                        class="reaction-overview__status"
                        aria-label="Status en opvolging wijzigen"
                        @click="editReactionFollowUp"
                    >
                        <span>{{ status.title }}</span>
                        <i class="ak-icon" aria-hidden="true">edit</i>
                    </button>
                    <span
                        v-if="reaction.nextActionAt"
                        :class="{ 'reaction-overview__next-action--required': reaction.actionRequired }"
                        class="reaction-overview__next-action"
                    >
                        <i class="ak-icon" aria-hidden="true">event</i>
                        {{ reaction.actionRequired ? 'Actie vereist sinds' : 'Volgende actie op' }}
                        {{ reaction.nextActionAt }}
                    </span>
                </div>
            </header>

            <section class="reaction-overview__tags" aria-label="Tags">
                <ul class="reaction-overview__tag-list">
                    <li v-for="tag in tags" :key="tag.id">
                        <span class="reaction-overview__tag" :style="{ backgroundColor: tag.color, color: getTagTextColor(tag.color) }">
                            {{ tag.title }}
                        </span>
                    </li>
                    <li>
                        <button type="button" class="reaction-overview__tag reaction-overview__tag--add" @click="editReactionTags">
                            <i class="ak-icon" aria-hidden="true">add</i>
                            {{ tags.length ? 'Tags bewerken' : 'Tags toevoegen' }}
                        </button>
                    </li>
                </ul>
            </section>

            <div class="reaction-overview__grid">
                <section class="reaction-overview__card reaction-overview__card--message" aria-labelledby="application-title">
                    <div class="reaction-overview__section-heading">
                        <p class="reaction-overview__eyebrow">Sollicitatie</p>
                        <h2 id="application-title">Over deze kandidatuur</h2>
                    </div>

                    <dl class="reaction-overview__details-list reaction-overview__details-list--on-dark">
                        <div class="reaction-overview__detail">
                            <dt>Referentienummer</dt>
                            <dd>{{ reaction.referenceNumber || '-' }}</dd>
                        </div>
                        <div class="reaction-overview__detail">
                            <dt>Ontvangen op</dt>
                            <dd>{{ reaction.createdAt || '-' }}</dd>
                        </div>
                        <div class="reaction-overview__detail">
                            <dt>Volgende actie</dt>
                            <dd>
                                {{ reaction.nextActionAt || 'Niet ingepland' }}
                                <span v-if="reaction.actionRequired" class="reaction-overview__action-required">
                                    Actie vereist
                                </span>
                            </dd>
                        </div>
                        <div class="reaction-overview__detail">
                            <dt>Kandidaat</dt>
                            <dd>{{ candidateName }}</dd>
                        </div>
                        <div
                            v-for="scoreCriterion in scoreCriteria"
                            :key="scoreCriterion.key"
                            class="reaction-overview__detail"
                        >
                            <dt>{{ scoreCriterion.label }}</dt>
                            <dd>
                                <div
                                    class="reaction-overview__score-control"
                                    role="group"
                                    :aria-label="`${scoreCriterion.label} voor deze sollicitatie`"
                                >
                                    <button
                                        v-for="score in scoreOptions"
                                        :key="score"
                                        type="button"
                                        :class="{ 'reaction-overview__score-button--active': reaction[scoreCriterion.key] === score }"
                                        class="reaction-overview__score-button"
                                        :disabled="isUpdatingScore[scoreCriterion.key]"
                                        @click="updateScore(scoreCriterion.key, score)"
                                    >
                                        {{ score }}
                                    </button>
                                </div>
                                <span class="reaction-overview__score-value">
                                    {{ reaction[scoreCriterion.key] === null ? 'Nog niet beoordeeld' : `${reaction[scoreCriterion.key]}/10` }}
                                </span>
                                <span
                                    v-if="scoreErrorMessages[scoreCriterion.key]"
                                    class="reaction-overview__score-error"
                                >
                                    {{ scoreErrorMessages[scoreCriterion.key] }}
                                </span>
                            </dd>
                        </div>
                        <div class="reaction-overview__detail">
                            <dt>Afgerond op</dt>
                            <dd>{{ reaction.completedAt || '-' }}</dd>
                        </div>
                    </dl>
                </section>

                <aside class="reaction-overview__card reaction-overview__card--details" aria-labelledby="details-title">
                    <div class="reaction-overview__section-heading">
                        <p class="reaction-overview__eyebrow">Contact</p>
                        <div class="reaction-overview__section-title">
                            <h2 id="details-title">Gegevens</h2>
                            <span v-if="candidate.isInternalCandidate" class="reaction-overview__internal-label">Interne kandidaat</span>
                        </div>
                    </div>

                    <dl class="reaction-overview__details-list">
                        <div v-for="detail in contactDetails" :key="detail.label" class="reaction-overview__detail">
                            <dt>{{ detail.label }}</dt>
                            <dd>
                                <a v-if="detail.href" :href="detail.href">{{ detail.value }}</a>
                                <template v-else>{{ detail.value }}</template>
                            </dd>
                        </div>
                    </dl>
                </aside>

                <section class="reaction-overview__card reaction-overview__card--documents" aria-labelledby="documents-title">
                    <div class="reaction-overview__section-heading">
                        <p class="reaction-overview__eyebrow">Bestanden</p>
                        <h2 id="documents-title">Documenten</h2>
                    </div>

                    <dl
                        v-if="documentGroups.length"
                        class="reaction-overview__document-groups"
                        aria-labelledby="documents-title"
                    >
                        <div
                            v-for="group in documentGroups"
                            :key="group.type"
                            class="reaction-overview__detail reaction-overview__document-group"
                        >
                            <dt>{{ group.label }}</dt>
                            <dd>
                                <ul class="reaction-overview__document-list">
                                    <li
                                        v-for="document in group.documents"
                                        :key="document.downloadLink"
                                        class="reaction-overview__document-list-item"
                                    >
                                        <a
                                            class="reaction-overview__document-name"
                                            :href="document.downloadLink"
                                            target="_blank"
                                            rel="noopener"
                                        >
                                            <span>{{ document.filenameOriginal }}</span>
                                            <i class="ak-icon" aria-hidden="true">download</i>
                                        </a>
                                    </li>
                                </ul>
                            </dd>
                        </div>
                    </dl>
                    <p v-else class="reaction-overview__empty">Geen documenten beschikbaar.</p>
                </section>

                <section class="reaction-overview__card reaction-overview__card--remarks" aria-labelledby="remarks-title">
                    <div class="reaction-overview__section-heading reaction-overview__section-heading--with-action">
                        <div>
                            <p class="reaction-overview__eyebrow">Intern</p>
                            <h2 id="remarks-title">Opmerkingen en contactverslagen</h2>
                        </div>
                        <button type="button" class="reaction-overview__remark-add-button" @click="addReactionRemark">
                            <i class="ak-icon" aria-hidden="true">add</i>
                            Opmerking / verslag toevoegen
                        </button>
                    </div>

                    <ol class="reaction-overview__remarks">
                        <li v-for="(remark, index) in internalRemarks" :key="remark.id || index" class="reaction-overview__remark">
                            <header class="reaction-overview__remark-header">
                                <div>
                                    <h3>{{ remark.title || 'Interne opmerking' }}</h3>
                                    <p>
                                        <span v-if="remark.cmsUser">{{ remark.cmsUser.name }}</span>
                                        <span v-if="remark.cmsUser && remark.createdAt" aria-hidden="true"> · </span>
                                        <span>{{ remark.createdAt || 'Datum onbekend' }}</span>
                                    </p>
                                </div>
                                <div v-if="remark.statusUpdate" class="reaction-overview__remark-actions">
                                    <div v-if="remark.statusUpdate" class="reaction-overview__remark-status-update">
                                        <span class="reaction-overview__remark-status">{{ remark.statusUpdate.from }}</span>
                                        <i class="ak-icon" aria-hidden="true">arrow_forward</i>
                                        <span class="reaction-overview__remark-status">{{ remark.statusUpdate.to }}</span>
                                    </div>
                                </div>
                            </header>

                            <div class="reaction-overview__remark-content" v-html="remark.remark"></div>
                            <ul v-if="remark.documents && remark.documents.length" class="reaction-overview__remark-documents">
                                <li v-for="document in remark.documents" :key="document.downloadLink">
                                    <a
                                        :href="document.downloadLink"
                                        class="reaction-overview__remark-document"
                                        target="_blank"
                                        rel="noopener"
                                    >
                                        <i class="ak-icon reaction-overview__remark-icon" aria-hidden="true">description</i>
                                        <span>{{ document.filenameOriginal }}</span>
                                        <i class="ak-icon" aria-hidden="true">download</i>
                                    </a>
                                </li>
                            </ul>
                        </li>
                        <li v-if="!internalRemarks.length" class="reaction-overview__empty">
                            Nog geen interne opmerkingen of contactverslagen toegevoegd.
                        </li>
                    </ol>
                </section>

                <section class="reaction-mail-overview reaction-overview__card" aria-labelledby="mails-title">
                    <div class="reaction-overview__section-heading reaction-overview__section-heading--with-action">
                        <div>
                            <p class="reaction-overview__eyebrow">Communicatie</p>
                            <h2 id="mails-title">E-mails</h2>
                        </div>
                        <button type="button" class="reaction-overview__remark-add-button" @click="addReactionMail">
                            <i class="ak-icon" aria-hidden="true">add</i>
                            Nieuwe mail opstellen
                        </button>
                    </div>

                    <div v-if="mails.length" class="reaction-mail-overview__table-wrap">
                        <table class="reaction-mail-overview__table">
                            <caption>Overzicht van inkomende en uitgaande e-mails</caption>
                            <thead>
                                <tr>
                                    <th scope="col">In/uit</th>
                                    <th scope="col">Datum</th>
                                    <th scope="col">Onderwerp</th>
                                    <th scope="col">Bericht</th>
                                    <th scope="col">Bijlagen</th>
                                    <th scope="col">Opvolging</th>
                                    <th scope="col"><span class="reaction-mail-overview__visually-hidden">Acties</span></th>
                                </tr>
                            </thead>
                            <tbody>
                                <tr v-for="mail in mails" :key="mail.id">
                                    <td>
                                        <span class="reaction-mail-overview__direction" :class="`reaction-mail-overview__direction--${mail.direction}`">
                                            <i class="ak-icon" aria-hidden="true">
                                                {{ mail.direction === 'incoming' ? 'south_west' : 'north_east' }}
                                            </i>
                                            {{ mail.direction === 'incoming' ? 'In' : 'Uit' }}
                                        </span>
                                    </td>
                                    <td><time>{{ mail.createdAt || '-' }}</time></td>
                                    <td class="reaction-mail-overview__subject">{{ mail.subject || 'Zonder onderwerp' }}</td>
                                    <td class="reaction-mail-overview__summary">{{ mail.summary || '-' }}</td>
                                    <td>
                                        <ul v-if="mail.attachments.length" class="reaction-mail-overview__attachments">
                                            <li v-for="attachment in mail.attachments" :key="attachment.downloadLink">
                                                <a :href="attachment.downloadLink" target="_blank" rel="noopener">
                                                    <i class="ak-icon" aria-hidden="true">attach_file</i>
                                                    <span>{{ attachment.filenameOriginal }}</span>
                                                    <i class="ak-icon" aria-hidden="true">download</i>
                                                </a>
                                            </li>
                                        </ul>
                                        <span v-else class="reaction-mail-overview__no-attachments">-</span>
                                    </td>
                                    <td class="reaction-mail-overview__follow-up">
                                        <template v-if="mail.direction === 'incoming'">
                                            <button
                                                type="button"
                                                class="reaction-mail-overview__process-button"
                                                :disabled="mail.isProcessed || isMailProcessing(mail)"
                                                @click="markMailProcessed(mail)"
                                            >
                                                {{ isMailProcessing(mail) ? 'Bezig…' : (mail.isProcessed ? 'Verwerkt' : 'Markeer als verwerkt') }}
                                            </button>
                                            <span v-if="mail.processingError" class="reaction-mail-overview__process-error" role="alert">
                                                {{ mail.processingError }}
                                            </span>
                                        </template>
                                        <span v-else class="reaction-mail-overview__no-follow-up">-</span>
                                    </td>
                                    <td class="reaction-mail-overview__actions">
                                        <button type="button" class="reaction-mail-overview__view-button" @click="viewReactionMail(mail)">
                                            Bekijken
                                            <i class="ak-icon" aria-hidden="true">chevron_right</i>
                                        </button>
                                    </td>
                                </tr>
                            </tbody>
                        </table>
                    </div>
                    <p v-else class="reaction-overview__empty">
                        Nog geen inkomende of uitgaande e-mails beschikbaar.
                    </p>
                </section>

                <section class="reaction-overview__card reaction-overview__card--timeline" aria-labelledby="timeline-title">
                    <div class="reaction-overview__section-heading">
                        <p class="reaction-overview__eyebrow">Overzicht</p>
                        <h2 id="timeline-title">Historiek</h2>
                    </div>

                    <ul class="reaction-overview__timeline">
                        <li v-for="(log, index) in logs" :key="`${log.createdAt}-${index}`" class="reaction-overview__timeline-item">
                            <span>{{ log.createdAt || '-' }}</span>
                            <strong>{{ log.description || 'Sollicitatie bijgewerkt' }}</strong>
                            <span v-if="log.cmsUser" class="reaction-overview__timeline-user">
                                <i class="ak-icon" aria-hidden="true">person</i>
                                {{ log.cmsUser.name }}
                            </span>
                        </li>
                        <li v-if="!logs.length" class="reaction-overview__empty">Nog geen historiek beschikbaar.</li>
                    </ul>
                </section>
            </div>
        </div>
      <router-view
          name="childview"
          @reactionSaved="reactionSaved"
      />
    </main>
</template>

<script>
export default {
    name: 'reactionOverview',
    data() {
        return {
            reaction: null,
            candidate: null,
            job: null,
            status: null,
            canChangeJob: false,
            isUpdatingScore: {
                score: false,
                technicalKnowledgeScore: false,
                organisationFitScore: false,
            },
            scoreErrorMessages: {
                score: null,
                technicalKnowledgeScore: null,
                organisationFitScore: null,
            },
            documentGroups: [],
            internalRemarks: [],
            mails: [],
            processingMailIds: [],
            tags: [],
            logs: [],
            loading: true,
            errorMessage: null,
        };
    },
    computed: {
        candidateName() {
            if (this.reaction && this.reaction.snapshotCandidateName) {
                return this.reaction.snapshotCandidateName;
            }

            return this.candidate && this.candidate.name
                ? this.candidate.name
                : '-';
        },
        scoreOptions() {
            return Array.from({ length: 10 }, (_, index) => index + 1);
        },
        scoreCriteria() {
            return [
                { key: 'score', label: 'Algemene score' },
                { key: 'technicalKnowledgeScore', label: 'Technische kennis' },
                { key: 'organisationFitScore', label: 'Fit met organisatie' },
            ];
        },
        contactDetails() {
            const detailList = [
                { label: 'E-mail', value: this.candidate.email || '-', href: this.candidate.email ? `mailto:${this.candidate.email}` : null },
                { label: 'Telefoon', value: this.candidate.phone || '-', href: this.candidate.phone ? `tel:${this.candidate.phone.replace(/\s/g, '')}` : null },
            ];

            if (this.reaction.jobTypeInterests && this.reaction.jobTypeInterests.length) {
                detailList.push({ label: 'Op zoek naar', value: this.reaction.jobTypeInterests.join(', '), href: null });
            }

            if (this.reaction.interests && this.reaction.interests.length) {
                detailList.push({ label: 'Interesses', value: this.reaction.interests.join(', '), href: null });
            }

            return detailList;
        },
    },
    methods: {
        async getReaction() {
            this.loading = true;
            this.errorMessage = null;

            try {
                const result = await this.$get(`/reaction-data/${this.$route.params.hashId}`);

                if ( ! result.success) {
                    this.errorMessage = result.errorMessage || 'De sollicitatie kon niet worden geladen.';
                    return;
                }

                this.reaction = result.reaction;
                this.candidate = result.candidate;
                this.job = result.job;
                this.canChangeJob = Boolean(result.permissions && result.permissions.canChangeJob);
                this.status = result.status;
                this.documentGroups = result.documentGroups || [];
                this.internalRemarks = result.internalRemarks || [];
                this.mails = result.mails || [];
                this.tags = result.tags || [];
                this.logs = result.logs || [];
            } catch (error) {
                this.errorMessage = 'De sollicitatie kon niet worden geladen.';
            } finally {
                this.loading = false;
            }
        },
        async updateScore(scoreType, score) {
            if (this.reaction[scoreType] === score) {
                return;
            }

            this.isUpdatingScore[scoreType] = true;
            this.scoreErrorMessages[scoreType] = null;

            try {
                const result = await this.$post(`/reaction-data/${this.$route.params.hashId}/score`, {
                    scoreType,
                    score,
                });

                if ( ! result.success) {
                    this.scoreErrorMessages[scoreType] = result.errorMessage || 'De score kon niet worden bijgewerkt.';
                    return;
                }

                this.reaction[scoreType] = result.score;

                if (result.log) {
                    this.logs.unshift(result.log);
                }
            } catch (error) {
                this.scoreErrorMessages[scoreType] = 'De score kon niet worden bijgewerkt.';
            } finally {
                this.isUpdatingScore[scoreType] = false;
            }
        },
        addReactionRemark() {
            this.$router.push({
              name: 'reaction-overview-form',
              params: {bundle: 'reaction', definition: 'remark-form', objectId: this.$route.params.hashId}
            });
        },

        addReactionMail() {
            this.$router.push({
                name: 'reaction-overview-form',
                params: { bundle: 'reaction', definition: 'mail-form', objectId: this.$route.params.hashId },
            });
        },

        viewReactionMail(mail) {
            this.$router.push({
                name: 'reaction-overview-form',
                params: { bundle: 'reaction-mail', definition: 'detail-form', objectId: mail.objectId },
            });
        },

        isMailProcessing(mail) {
            return this.processingMailIds.includes(mail.id);
        },

        async markMailProcessed(mail) {
            if (mail.direction !== 'incoming' || mail.isProcessed || this.isMailProcessing(mail)) {
                return;
            }

            this.processingMailIds.push(mail.id);
            this.$set(mail, 'processingError', null);

            try {
                const result = await this.$post(`/reaction-mail/${mail.objectId}/processed`, {});
                if ( ! result.success) {
                    this.$set(mail, 'processingError', result.errorMessage || 'De e-mail kon niet worden verwerkt.');
                    return;
                }

                this.$set(mail, 'isProcessed', true);
                if (result.log) {
                    this.logs.unshift(result.log);
                }
            } catch (error) {
                this.$set(mail, 'processingError', 'De e-mail kon niet worden verwerkt.');
            } finally {
                this.processingMailIds = this.processingMailIds.filter((mailId) => mailId !== mail.id);
            }
        },

        editReactionTags() {
            this.$router.push({
                name: 'reaction-overview-form',
                params: { bundle: 'reaction', definition: 'tag-form', objectId: this.$route.params.hashId },
            });
        },

        editReactionFollowUp() {
            this.$router.push({
                name: 'reaction-overview-form',
                params: {
                    bundle: 'reaction',
                    definition: 'follow-up-form',
                    objectId: this.$route.params.hashId,
                },
            });
        },

        editReactionJob() {
            this.$router.push({
                name: 'reaction-overview-form',
                params: {
                    bundle: 'reaction',
                    definition: 'job-form',
                    objectId: this.$route.params.hashId,
                    relationString: 'jobSelection:grid:job:relation-grid',
                },
            });
        },

        getTagTextColor(color) {
            const hex = String(color || '').replace('#', '');
            const normalizedHex = hex.length === 3
                ? hex.split('').map((character) => character + character).join('')
                : hex;

            if (!/^[0-9a-f]{6}$/i.test(normalizedHex)) {
                return '#1e1e1e';
            }

            const rgb = [0, 2, 4].map((index) => parseInt(normalizedHex.slice(index, index + 2), 16) / 255);
            const luminance = rgb
                .map((value) => (value <= 0.03928 ? value / 12.92 : ((value + 0.055) / 1.055) ** 2.4))
                .reduce((total, value, index) => total + value * [0.2126, 0.7152, 0.0722][index], 0);

            return luminance > 0.179 ? '#1e1e1e' : '#ffffff';
        },

        reactionSaved() {
            const { hashId } = this.$route.params;

            this.getReaction();
            this.$router.push({
              name: 'reaction-overview',
              params: { hashId },
            });
        }

    },
    created() {
        this.getReaction();
    },
}
</script>
